import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, Loader2, X, Search, ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Image compression ────────────────────────────────────────────────────────
const MAX_B64_CHARS = 700000;

function compressImage(dataUrl, quality = 0.6, maxDim = 1024) {
    return new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.onload = () => {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const ratio = Math.min(maxDim / width, maxDim / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = dataUrl;
    });
}

async function compressToSafe(dataUrl) {
    let result = await compressImage(dataUrl, 0.6, 1024);
    if (result.length > MAX_B64_CHARS) result = await compressImage(dataUrl, 0.4, 800);
    if (result.length > MAX_B64_CHARS) result = await compressImage(dataUrl, 0.25, 640);
    const b64 = result.split(',')[1] || '';
    const kb = Math.round(b64.length * 0.75 / 1024);
    return { url: result, base64: b64, kb };
}

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ImageUploader({ onImageCapture, isProcessing }) {
    const galleryInputRef = useRef(null);
    const debounceTimer = useRef(null);

    const [image, setImage] = useState(null);       // { url, base64, kb }
    const [searchText, setSearchText] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const clearAll = useCallback(() => {
        setImage(null);
        setSearchText('');
        setErrorMsg(null);
        setIsAnalyzing(false);
    }, []);

    // ── Run analysis via image ─────────────────────────────────────────────────
    const analyzeImage = useCallback(async (compressed) => {
        if (isProcessing || isAnalyzing) return;
        setIsAnalyzing(true);
        setErrorMsg(null);
        try {
            await Promise.resolve(onImageCapture([compressed.base64], clearAll));
        } catch (err) {
            setErrorMsg(err?.message || 'Error al analizar la imagen');
        } finally {
            setIsAnalyzing(false);
        }
    }, [isProcessing, isAnalyzing, onImageCapture, clearAll]);

    // ── Run analysis via text ──────────────────────────────────────────────────
    const analyzeText = useCallback(async (text) => {
        if (!text?.trim() || isProcessing || isAnalyzing) return;
        setIsAnalyzing(true);
        setErrorMsg(null);
        // Encode text as a pseudo base64 flag so Home can handle it
        // We pass a special marker so Home knows it's a text search
        try {
            await Promise.resolve(onImageCapture([], clearAll, text.trim()));
        } catch (err) {
            setErrorMsg(err?.message || 'Error al buscar el producto');
        } finally {
            setIsAnalyzing(false);
        }
    }, [isProcessing, isAnalyzing, onImageCapture, clearAll]);

    // ── Handle text input with debounce ───────────────────────────────────────
    const handleTextChange = (e) => {
        const val = e.target.value;
        setSearchText(val);
        setErrorMsg(null);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        if (val.trim().length >= 4) {
            debounceTimer.current = setTimeout(() => {
                analyzeText(val);
            }, 1300);
        }
    };

    const handleTextKeyDown = (e) => {
        if (e.key === 'Enter' && searchText.trim().length >= 4) {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            analyzeText(searchText);
        }
    };

    useEffect(() => {
        return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
    }, []);

    // ── Handle gallery file input ──────────────────────────────────────────────
    const [isCompressing, setIsCompressing] = useState(false);

    const handleFileInput = async (e) => {
        const file = Array.from(e.target.files || []).find(f => f.type.startsWith('image/'));
        e.target.value = '';
        if (!file) return;
        setErrorMsg(null);
        setIsCompressing(true);
        try {
            const dataUrl = await readFileAsDataUrl(file);
            const compressed = await compressToSafe(dataUrl);
            if (compressed.base64.length > MAX_B64_CHARS) {
                setErrorMsg('La imagen es demasiado grande. Intentá con otra.');
                return;
            }
            setImage(compressed);
            setSearchText('');
            setIsCompressing(false);
            await analyzeImage(compressed);
        } catch (err) {
            setErrorMsg('Error al procesar la imagen. Intentá con otra.');
        } finally {
            setIsCompressing(false);
        }
    };

    const isLoading = isProcessing || isAnalyzing || isCompressing;

    return (
        <div className="w-full space-y-3">
            {/* Hidden gallery input */}
            <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                style={{ display: 'none' }}
            />

            {/* Error banner */}
            <AnimatePresence>
                {errorMsg && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="px-3 py-2 bg-red-500/80 rounded-xl text-white text-xs flex items-start gap-2"
                    >
                        <span>⚠️</span>
                        <span className="flex-1">{errorMsg}</span>
                        <button onClick={() => setErrorMsg(null)}>✕</button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Loading state */}
            <AnimatePresence>
                {isLoading && (
                    <motion.div
                        key="loading"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex flex-col items-center justify-center h-32 bg-white/20 rounded-2xl border-2 border-white/40"
                    >
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-white/30 flex items-center justify-center">
                                <Loader2 className="w-6 h-6 text-white animate-spin" />
                            </div>
                            <motion.div
                                className="absolute inset-0 rounded-full border-2 border-white"
                                animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            />
                        </div>
                        <p className="mt-3 text-white font-semibold text-sm">{isCompressing ? 'Procesando imagen...' : 'Analizando producto...'}</p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main UI — only show when not loading */}
            {!isLoading && (
                <>
                    {/* Text search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60 pointer-events-none" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={handleTextChange}
                            onKeyDown={handleTextKeyDown}
                            placeholder="Escribí el nombre del producto..."
                            className="w-full bg-white/20 border border-white/40 rounded-xl pl-9 pr-4 py-3 text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/80 focus:bg-white/30 transition-all"
                        />
                        {searchText && (
                            <button
                                onClick={() => { setSearchText(''); setErrorMsg(null); if (debounceTimer.current) clearTimeout(debounceTimer.current); }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    {searchText.trim().length >= 4 && (
                        <p className="text-white/60 text-xs text-center -mt-1">Analizando en unos segundos... (o presioná Enter)</p>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-px bg-white/20" />
                        <span className="text-white/40 text-xs">o</span>
                        <div className="flex-1 h-px bg-white/20" />
                    </div>

                    {/* Image from gallery */}
                    {image ? (
                        <div className="relative rounded-xl overflow-hidden bg-white/10 aspect-video">
                            <img src={image.url} alt="Etiqueta" className="w-full h-full object-contain" />
                            <button
                                onClick={() => setImage(null)}
                                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 flex items-center justify-center shadow-md"
                            >
                                <X className="w-4 h-4 text-white" />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/50 text-center">
                                <span className="text-white text-[10px]">{image.kb} KB</span>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => galleryInputRef.current?.click()}
                            className="w-full border-2 border-dashed border-white/50 rounded-xl py-5 flex flex-col items-center gap-2 hover:bg-white/10 active:bg-white/20 transition-all"
                        >
                            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-white" />
                            </div>
                            <p className="text-white font-semibold text-sm">Subir foto de etiqueta</p>
                            <p className="text-white/60 text-xs">Se analizará automáticamente</p>
                        </button>
                    )}
                </>
            )}
        </div>
    );
}